import { useEffect, useRef, useState } from 'react';
import { readError } from '../../app/api';
import { loadCountries } from './analytics-api';
import type { AnalyticsFilterScope } from './AnalyticsFilterScope';
import { countryFlag, formatCountryName } from './GeoUi';
import type { CountryOption } from './types';

export function CountryMultiFilter(props: {
  environmentId: string;
  filters: AnalyticsFilterScope;
  selectedCountries: readonly string[];
  onChange: (countries: string[]) => void;
}): React.JSX.Element {
  const details = useRef<HTMLDetailsElement>(null);
  const [options, setOptions] = useState<CountryOption[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent): void {
      const picker = details.current;
      if (picker !== null && picker.open && event.target instanceof Node && !picker.contains(event.target)) {
        picker.open = false;
      }
    }

    document.addEventListener('click', closeOnOutsideClick);
    return () => { document.removeEventListener('click', closeOnOutsideClick); };
  }, []);

  useEffect(() => {
    let active = true;
    setOptions([]);
    setSearch('');
    setLoading(true);
    setError('');
    loadCountries(props.environmentId, props.filters)
      .then((response) => { if (active) setOptions([...response].sort(compareCountries)); })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [
    props.environmentId,
    props.filters.preset,
    props.filters.from,
    props.filters.to,
    props.filters.platforms,
    props.filters.appVersions,
  ]);

  const selectedCount = props.selectedCountries.length;
  const summary = selectedCount === 0
    ? 'All countries'
    : props.selectedCountries.map(formatCountryLabel).join(', ');
  const knownCountryCodes = new Set(options.map(readCountryCode));
  const unavailableSelections = props.selectedCountries.filter((countryCode) => !knownCountryCodes.has(countryCode));
  const normalizedSearch = search.trim().toLocaleLowerCase('en');
  const visibleOptions = options.filter((option) => matchesSearch(option.countryCode, normalizedSearch));
  const visibleUnavailableSelections = unavailableSelections.filter((countryCode) => matchesSearch(countryCode, normalizedSearch));
  const hasVisibleCountry = visibleOptions.length > 0 || visibleUnavailableSelections.length > 0;

  return (
    <section className="analytics-option-filter country-filter" aria-label="Countries included">
      <strong>Countries</strong>
      <details className="analytics-option-picker" ref={details} onToggle={resetSearchWhenClosed}>
        <summary title={selectedCount > 0 ? summary : undefined}>
          <span>{summary}</span>
          <small>{selectedCount === 0 ? 'No country filter' : `${formatNumber(selectedCount)} selected`}</small>
        </summary>
        <div className="analytics-option-menu">
          <div className="country-menu-tools">
            <header className="analytics-option-menu-header"><span>Tracked countries</span><button type="button" onClick={close}>Done</button></header>
            <label className="country-filter-search">
              <input type="search" value={search} placeholder="Search countries" aria-label="Search tracked countries" autoComplete="off" spellCheck={false} onChange={changeSearch} />
            </label>
          </div>
          <label className="analytics-option all-options">
            <input type="checkbox" checked={selectedCount === 0} onChange={showAll} />
            <span><strong>All countries</strong><small>{loading ? 'Loading tracked countries…' : `${formatNumber(options.length)} available`}</small></span>
          </label>
          {visibleOptions.map(renderOption)}
          {visibleUnavailableSelections.map(renderUnavailableSelection)}
          {!loading && normalizedSearch === '' && options.length === 0 && error === '' && <p>No countries match the other filters.</p>}
          {!loading && normalizedSearch !== '' && !hasVisibleCountry && error === '' && <p>No countries match “{search.trim()}”.</p>}
          {error !== '' && <p title={error}>Tracked countries could not be loaded.</p>}
        </div>
      </details>
    </section>
  );

  function showAll(): void {
    props.onChange([]);
  }

  function toggle(countryCode: string): void {
    const selected = props.selectedCountries.includes(countryCode);
    const next = selected
      ? props.selectedCountries.filter((entry) => entry !== countryCode)
      : [...props.selectedCountries, countryCode];
    props.onChange(next);
  }

  function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearch(event.target.value);
  }

  function close(): void {
    if (details.current !== null) details.current.open = false;
    setSearch('');
  }

  function resetSearchWhenClosed(): void {
    if (details.current?.open === false) setSearch('');
  }

  function renderOption(option: CountryOption): React.JSX.Element {
    const selected = props.selectedCountries.includes(option.countryCode);
    return (
      <label className="analytics-option" key={option.countryCode}>
        <input type="checkbox" checked={selected} onChange={() => toggle(option.countryCode)} />
        <span><strong>{formatCountryLabel(option.countryCode)}</strong><small>{option.countryCode} · {formatNumber(option.events)} events · {formatNumber(option.sessions)} sessions</small></span>
      </label>
    );
  }

  function renderUnavailableSelection(countryCode: string): React.JSX.Element {
    return (
      <label className="analytics-option unavailable" key={countryCode}>
        <input type="checkbox" checked onChange={() => toggle(countryCode)} />
        <span><strong>{formatCountryLabel(countryCode)}</strong><small>{countryCode} · Not available with the other filters</small></span>
      </label>
    );
  }
}

function matchesSearch(countryCode: string, normalizedSearch: string): boolean {
  if (normalizedSearch === '') return true;
  return countryCode.toLocaleLowerCase('en').includes(normalizedSearch)
    || formatCountryName(countryCode).toLocaleLowerCase('en').includes(normalizedSearch);
}

function compareCountries(left: CountryOption, right: CountryOption): number {
  return formatCountryName(left.countryCode).localeCompare(formatCountryName(right.countryCode), 'en');
}

function formatCountryLabel(countryCode: string): string {
  return `${countryFlag(countryCode)} ${formatCountryName(countryCode)}`;
}

function readCountryCode(option: CountryOption): string {
  return option.countryCode;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
